/**
 * The tablet's vault: its own registration, and a cache of every person's
 * PIN-wrapped credential.
 *
 * Decided by the owner on 23 September 2026: he sets a person's PIN when he
 * creates the login, and that person then uses any registered tablet — tap
 * your name, type your PIN. So:
 *
 *   set a PIN    (on the owner's phone) mint 32 random bytes — the person's
 *                credential C. Derive K = PBKDF2-SHA256(PIN, salt, 310k) and
 *                wrap it, AES-GCM(K, C). Send the server the wrapped blob and
 *                SHA-256(C). Never the PIN.
 *   cache        a registered tablet fetches every login's blob
 *                (GET /auth/logins) and keeps it here, so unlock works with no
 *                signal.
 *   unlock       re-derive K from the typed PIN and decrypt the cached blob.
 *                A wrong PIN fails the GCM authentication tag — locally,
 *                offline, with no round trip. C is returned to the caller,
 *                used once to get a session token, and never held anywhere.
 *
 * Five wrong PINs lock that person out on this tablet until the owner sets
 * them a new PIN (the blob's hash changes, which clears the lock). Every
 * other tablet keeps its own count — five tries per tablet, not five in all.
 *
 * Nothing here writes the PIN, or anything derived from it, to storage.
 *
 * Threat model, stated plainly (CLAUDE.md, "How sign-in actually works"): a
 * 4-digit PIN is 10,000 possibilities, so anyone holding a wrapped blob — a
 * tablet's storage, or a leak of the server's table — can grind it offline.
 * The owner accepted that in exchange for setting PINs centrally. The PIN
 * stops a passer-by at the tablet, not a forensic attacker.
 */

const DB_NAME = 'quarry-device';
/**
 * v1 held one per-device record under a fixed key; v2 keyed per-device records
 * by device id; v3 added the tablet store; v4 keys the vault by PERSON, caching
 * the server's PIN-wrapped credentials. A v4 upgrade clears the old per-device
 * records — they are unusable under the new model and the next fetch fills the
 * cache — but keeps the tablet registration.
 */
const DB_VERSION = 4;
const STORE = 'vault';
const TABLET_STORE = 'tablet';
const TABLET_KEY = 'tablet';

/** OWASP's 2023 floor for PBKDF2-SHA256. Stored per record so it can be raised. */
const PBKDF2_ITERATIONS = 310_000;

/** "Lock out after 5 failed attempts." */
export const MAX_PIN_ATTEMPTS = 5;

/** What the server holds for a person, and what a tablet caches. All base64url. */
export interface PinWrap {
  credentialHash: string;
  wrapped: string;
  salt: string;
  iv: string;
  iterations: number;
}

interface PersonRecord {
  userId: string;
  name: string;
  role: 'owner' | 'clerk';
  /** null: the owner has not set this person a PIN yet. */
  pin: PinWrap | null;
  failedAttempts: number;
  /** The credential hash five wrong PINs were counted against. A new PIN clears it. */
  lockedHash: string | null;
  lastUnlockAt: string | null;
  cachedAt: string;
}

/** What the UI is allowed to know without a PIN. */
export interface PersonSummary {
  userId: string;
  name: string;
  role: 'owner' | 'clerk';
  hasPin: boolean;
  attemptsLeft: number;
  locked: boolean;
  lastUnlockAt: string | null;
}

export class PinError extends Error {
  readonly attemptsLeft: number;
  readonly wiped: boolean;

  constructor(attemptsLeft: number, wiped: boolean, who?: string) {
    super(
      wiped
        ? `Too many wrong PINs. ${who ?? 'This person'} is locked out of this tablet until the owner sets them a new PIN.`
        : `That PIN is not right. ${attemptsLeft} ${attemptsLeft === 1 ? 'try' : 'tries'} left.`,
    );
    this.name = 'PinError';
    this.attemptsLeft = attemptsLeft;
    this.wiped = wiped;
  }
}

// ------------------------------------------------------------- IndexedDB

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (event) => {
      const db = request.result;
      if (!db.objectStoreNames.contains(TABLET_STORE)) db.createObjectStore(TABLET_STORE);
      if (event.oldVersion < 4) {
        // The per-device records of v1–v3 mean nothing now. Start the cache clean.
        if (db.objectStoreNames.contains(STORE)) db.deleteObjectStore(STORE);
        db.createObjectStore(STORE);
      } else if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB is unavailable.'));
  });
}

async function withStore<T>(
  store: string,
  mode: IDBTransactionMode,
  work: (s: IDBObjectStore) => IDBRequest<T> | void,
): Promise<T> {
  const db = await openDb();
  try {
    return await new Promise<T>((resolve, reject) => {
      const tx = db.transaction(store, mode);
      const request = work(tx.objectStore(store));
      let result: T;
      if (request) {
        request.onsuccess = () => { result = request.result; };
        request.onerror = () => reject(request.error);
      }
      tx.oncomplete = () => resolve(result);
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}

const readPerson = (userId: string) =>
  withStore<PersonRecord | undefined>(STORE, 'readonly', (s) => s.get(userId)).then((r) => r ?? null);
const readAll = () =>
  withStore<PersonRecord[]>(STORE, 'readonly', (s) => s.getAll()).then((r) =>
    (r ?? []).filter((x) => x && x.userId && x.userId !== '__pad'));
const writePerson = (record: PersonRecord) =>
  withStore<IDBValidKey>(STORE, 'readwrite', (s) => s.put(record, record.userId)).then(() => undefined);
const deletePerson = (userId: string) =>
  withStore<undefined>(STORE, 'readwrite', (s) => s.delete(userId)).then(() => undefined);

// ---------------------------------------------------------------- crypto

const enc = new TextEncoder();

/**
 * `Uint8Array<ArrayBuffer>` rather than a bare `Uint8Array`: since TypeScript
 * 5.7 the latter is generic over `ArrayBufferLike`, which includes
 * `SharedArrayBuffer` and is therefore not a `BufferSource` for Web Crypto.
 */
function randomBytes(length: number): Uint8Array<ArrayBuffer> {
  const bytes = new Uint8Array(new ArrayBuffer(length));
  crypto.getRandomValues(bytes);
  return bytes;
}

function b64url(bytes: Uint8Array<ArrayBuffer> | ArrayBuffer): string {
  const view = bytes instanceof ArrayBuffer ? new Uint8Array(bytes) : bytes;
  let s = '';
  for (const b of view) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromB64url(text: string): ArrayBuffer {
  const padded = text.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (text.length % 4)) % 4);
  const bin = atob(padded);
  const out = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}

async function sha256Hex(value: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', enc.encode(value));
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

async function deriveKey(pin: string, salt: ArrayBuffer, iterations: number): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey('raw', enc.encode(pin), 'PBKDF2', false, [
    'deriveKey',
  ]);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations, hash: 'SHA-256' },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

// ------------------------------------------------------- setting a PIN

/**
 * Mints a credential and wraps it with the PIN. Pure: touches no storage. The
 * owner's phone sends the result to the server with the new login (or a PIN
 * reset); the recovery screen sends it for the owner himself. The credential
 * is returned too, for the one caller that signs in with it straight away.
 */
export async function preparePin(pin: string): Promise<{ credential: string; pin: PinWrap }> {
  assertPinShape(pin);
  const credential = b64url(randomBytes(32));
  const salt = randomBytes(16);
  const iv = randomBytes(12);
  const key = await deriveKey(pin, salt.buffer as ArrayBuffer, PBKDF2_ITERATIONS);
  const wrapped = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, enc.encode(credential));
  return {
    credential,
    pin: {
      credentialHash: await sha256Hex(credential),
      wrapped: b64url(wrapped),
      salt: b64url(salt),
      iv: b64url(iv),
      iterations: PBKDF2_ITERATIONS,
    },
  };
}

// ------------------------------------------------------------ the cache

function summarise(record: PersonRecord): PersonSummary {
  const locked = record.pin !== null && record.lockedHash === record.pin.credentialHash;
  return {
    userId: record.userId,
    name: record.name,
    role: record.role,
    hasPin: record.pin !== null,
    attemptsLeft: locked ? 0 : Math.max(0, MAX_PIN_ATTEMPTS - record.failedAttempts),
    locked,
    lastUnlockAt: record.lastUnlockAt,
  };
}

/**
 * What the server just said about who can log in. Upserts by person; a new PIN
 * (a different credential hash) resets that person's attempt count and lock;
 * anyone no longer listed is dropped.
 */
export async function cacheLogins(
  logins: { id: string; name: string; role: 'owner' | 'clerk'; pin: PinWrap | null }[],
): Promise<void> {
  const existing = new Map((await readAll()).map((r) => [r.userId, r]));
  const at = new Date().toISOString();
  const seen = new Set<string>();
  for (const login of logins) {
    seen.add(login.id);
    const old = existing.get(login.id);
    const samePin = old?.pin?.credentialHash === login.pin?.credentialHash;
    await writePerson({
      userId: login.id,
      name: login.name,
      role: login.role,
      pin: login.pin,
      failedAttempts: samePin ? old?.failedAttempts ?? 0 : 0,
      lockedHash: samePin ? old?.lockedHash ?? null : null,
      lastUnlockAt: old?.lastUnlockAt ?? null,
      cachedAt: at,
    });
  }
  for (const old of existing.values()) {
    if (!seen.has(old.userId)) await deletePerson(old.userId);
  }
}

/** Anyone cached at all? */
export async function hasPeople(): Promise<boolean> {
  try {
    return (await readAll()).length > 0;
  } catch {
    return false;
  }
}

/** Everyone who can log in here, most recently unlocked first, then owners, then by name. */
export async function listPeople(): Promise<PersonSummary[]> {
  return (await readAll())
    .map(summarise)
    .sort((a, b) => {
      if (a.lastUnlockAt !== b.lastUnlockAt) {
        if (!a.lastUnlockAt) return 1;
        if (!b.lastUnlockAt) return -1;
        return a.lastUnlockAt < b.lastUnlockAt ? 1 : -1;
      }
      if (a.role !== b.role) return a.role === 'owner' ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
}

export async function personSummary(userId: string): Promise<PersonSummary | null> {
  const record = await readPerson(userId);
  return record ? summarise(record) : null;
}

/**
 * Unwraps one person's credential with their PIN.
 *
 * Works with no signal: this is the "the device verifies the PIN against the
 * enrolled credential locally" half of the brief. The caller exchanges the
 * credential for a session token when there is a connection, and queues writes
 * when there is not.
 */
export async function unlock(userId: string, pin: string): Promise<string> {
  const record = await readPerson(userId);
  if (!record) throw new Error('That person is not on this tablet. Connect once so the tablet can fetch who can log in.');
  if (!record.pin) throw new Error(`${record.name} has no PIN yet. Ask the owner to set one.`);
  if (record.lockedHash === record.pin.credentialHash) throw new PinError(0, true, record.name);

  let credential: string;
  try {
    const key = await deriveKey(pin, fromB64url(record.pin.salt), record.pin.iterations);
    const plain = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: fromB64url(record.pin.iv) },
      key,
      fromB64url(record.pin.wrapped),
    );
    credential = new TextDecoder().decode(plain);
  } catch {
    // A wrong PIN fails the GCM tag. Count it, and at five lock THIS PERSON on
    // THIS TABLET until the owner sets them a new PIN. Everyone else is untouched.
    const failedAttempts = record.failedAttempts + 1;
    if (failedAttempts >= MAX_PIN_ATTEMPTS) {
      await writePerson({ ...record, failedAttempts, lockedHash: record.pin.credentialHash });
      throw new PinError(0, true, record.name);
    }
    await writePerson({ ...record, failedAttempts });
    throw new PinError(MAX_PIN_ATTEMPTS - failedAttempts, false);
  }

  await writePerson({ ...record, failedAttempts: 0, lastUnlockAt: new Date().toISOString() });
  return credential;
}

// -------------------------------------------------------- PIN only

/**
 * "Can I just enter the PIN and the app decides which login?" — yes, and it is
 * what the brief said all along: "4-digit PIN login, no usernames". The pad
 * tries the PIN against every cached wrap, most recently used first, and the
 * first that opens is who you are. So PINs are unique across people
 * (`pinInUse` below refuses a duplicate when one is set), and five wrong PINs
 * lock the pad on this tablet — a wrong PIN is nobody's in particular — for
 * PAD_LOCK_MS.
 */
const PAD_KEY = '__pad';
const PAD_LOCK_MS = 15 * 60 * 1000;

interface PadRecord {
  userId: typeof PAD_KEY;
  failedAttempts: number;
  lockedUntil: string | null;
}

async function readPad(): Promise<PadRecord> {
  const raw = await withStore<PadRecord | undefined>(STORE, 'readonly', (s) => s.get(PAD_KEY));
  return raw ?? { userId: PAD_KEY, failedAttempts: 0, lockedUntil: null };
}

async function writePad(pad: PadRecord): Promise<void> {
  await withStore<IDBValidKey>(STORE, 'readwrite', (s) => s.put(pad, PAD_KEY));
}

export class PadLockedError extends Error {
  readonly until: string;
  constructor(until: string) {
    const minutes = Math.max(1, Math.ceil((Date.parse(until) - Date.now()) / 60_000));
    super(`Too many wrong PINs. Try again in ${minutes} minute${minutes === 1 ? '' : 's'}.`);
    this.name = 'PadLockedError';
    this.until = until;
  }
}

/** When the pad is locked, and how many tries are left when it is not. */
export async function padState(): Promise<{ lockedUntil: string | null; attemptsLeft: number }> {
  const pad = await readPad();
  if (pad.lockedUntil && pad.lockedUntil > new Date().toISOString()) {
    return { lockedUntil: pad.lockedUntil, attemptsLeft: 0 };
  }
  return { lockedUntil: null, attemptsLeft: Math.max(0, MAX_PIN_ATTEMPTS - pad.failedAttempts) };
}

async function tryOpen(record: PersonRecord, pin: string): Promise<string | null> {
  if (!record.pin) return null;
  try {
    const key = await deriveKey(pin, fromB64url(record.pin.salt), record.pin.iterations);
    const plain = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: fromB64url(record.pin.iv) },
      key,
      fromB64url(record.pin.wrapped),
    );
    return new TextDecoder().decode(plain);
  } catch {
    return null;
  }
}

/**
 * The PIN alone. Returns who it belongs to and their credential. Most recently
 * unlocked people are tried first, so the usual person costs one derivation.
 */
export async function unlockByPin(pin: string): Promise<{ userId: string; name: string; role: 'owner' | 'clerk'; credential: string }> {
  assertPinShape(pin);
  const pad = await readPad();
  const now = new Date().toISOString();
  if (pad.lockedUntil && pad.lockedUntil > now) throw new PadLockedError(pad.lockedUntil);

  const people = (await readAll())
    .filter((r) => r.pin !== null)
    .sort((a, b) => (b.lastUnlockAt ?? '') < (a.lastUnlockAt ?? '') ? -1 : 1);
  if (people.length === 0) {
    throw new Error('Nobody can log in on this tablet yet. Connect it to the office once so it can fetch who can log in.');
  }
  for (const record of people) {
    const credential = await tryOpen(record, pin);
    if (credential !== null) {
      await writePerson({ ...record, failedAttempts: 0, lastUnlockAt: now });
      if (pad.failedAttempts !== 0 || pad.lockedUntil) await writePad({ userId: PAD_KEY, failedAttempts: 0, lockedUntil: null });
      return { userId: record.userId, name: record.name, role: record.role, credential };
    }
  }

  const failedAttempts = pad.failedAttempts + 1;
  if (failedAttempts >= MAX_PIN_ATTEMPTS) {
    const until = new Date(Date.now() + PAD_LOCK_MS).toISOString();
    await writePad({ userId: PAD_KEY, failedAttempts: 0, lockedUntil: until });
    throw new PadLockedError(until);
  }
  await writePad({ userId: PAD_KEY, failedAttempts, lockedUntil: null });
  throw new PinError(MAX_PIN_ATTEMPTS - failedAttempts, false);
}

/**
 * Is this PIN already somebody's? Checked on the owner's phone before a PIN is
 * set, against the cached wraps. Returns true/false only — never whose, since
 * the owner typing it would otherwise learn that person's PIN.
 */
export async function pinInUse(pin: string, exceptUserId?: string): Promise<boolean> {
  for (const record of await readAll()) {
    if (record.userId === exceptUserId || record.userId === PAD_KEY) continue;
    if ((await tryOpen(record, pin)) !== null) return true;
  }
  return false;
}

/** Everyone cached. Used when the tablet itself is being handed on. */
export async function forgetAll(): Promise<void> {
  for (const record of await readAll()) await deletePerson(record.userId);
  await deletePerson(PAD_KEY);
}

// --------------------------------------------------------------- tablet

/**
 * This tablet's registration: a random 32-byte credential, stored as it is.
 * It is not wrapped by a PIN — it identifies the tablet, not a person, and the
 * brief's "long-lived credential (a random token in IndexedDB)" is exactly
 * this. The server holds only its hash. Presenting it is what lets the tablet
 * fetch who can log in, and what a person's PIN is accepted on.
 */
export interface TabletRecord {
  tabletId: string;
  label: string;
  credential: string;
  registeredAt: string;
}

export async function getTablet(): Promise<TabletRecord | null> {
  return withStore<TabletRecord | undefined>(TABLET_STORE, 'readonly', (s) => s.get(TABLET_KEY)).then((r) => r ?? null);
}

/** Mints the tablet credential; `seal` stores it once the server has accepted it. */
export async function prepareTablet(): Promise<{
  credential: string;
  credentialHash: string;
  seal: (identity: { tabletId: string; label: string }) => Promise<void>;
}> {
  const credential = b64url(randomBytes(32));
  return {
    credential,
    credentialHash: await sha256Hex(credential),
    seal: async (identity) => {
      await withStore<IDBValidKey>(TABLET_STORE, 'readwrite', (s) =>
        s.put({ ...identity, credential, registeredAt: new Date().toISOString() } satisfies TabletRecord, TABLET_KEY),
      );
    },
  };
}

/** The owner removed this tablet, or it is being handed on. Takes everyone with it. */
export async function forgetTablet(): Promise<void> {
  await forgetAll();
  await withStore<undefined>(TABLET_STORE, 'readwrite', (s) => s.delete(TABLET_KEY));
}

// ------------------------------------------------------------- PIN rules

export function assertPinShape(pin: string): void {
  if (!/^[0-9]{4}$/.test(pin)) throw new Error('A PIN is exactly 4 digits.');
}

/**
 * Refuses the handful of PINs that are genuinely guessable on a shared tablet.
 * Returns a reason, or null if it is fine.
 */
export function weakPinReason(pin: string): string | null {
  if (/^(\d)\1{3}$/.test(pin)) return 'Four of the same digit is too easy to guess.';
  const ascending = '0123456789';
  const descending = '9876543210';
  if (ascending.includes(pin) || descending.includes(pin)) {
    return 'Four digits in a row is too easy to guess.';
  }
  if (pin === '1234' || pin === '1111' || pin === '0000' || pin === '2580') {
    return 'That is one of the most common PINs. Pick another.';
  }
  return null;
}
